import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RecipeList = () => {
  const [recipes, setRecipes] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { token } = useAuth();

  useEffect(() => {
    fetchRecipes();
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await fetch('/api/tags', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAllTags(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const fetchRecipes = async (query = '', tagId = null) => {
    try {
      setLoading(true);
      let url = '/api/recipes';
      
      if (query) {
        url = `/api/recipes/search?q=${encodeURIComponent(query)}`;
      } else if (tagId) {
        url = `/api/recipes/search?tag=${tagId}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch recipes');
      }

      setRecipes(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSelectedTag(null);
    fetchRecipes(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedTag(null);
    fetchRecipes();
  };

  const handleTagFilter = (tag) => {
    if (selectedTag?.id === tag.id) {
      setSelectedTag(null);
      fetchRecipes();
    } else {
      setSelectedTag(tag);
      setSearchQuery('');
      fetchRecipes('', tag.id);
    }
  };

  if (loading && recipes.length === 0) {
    return <div style={styles.loading}>Loading recipes...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🍳 Recipes ({recipes.length})</h1>
        <Link to="/recipes/new" style={styles.createBtn}>+ New Recipe</Link>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={styles.searchForm}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, ingredient, or tag..."
          style={styles.searchInput}
        />
        <button type="submit" style={styles.searchBtn}>Search</button>
        {(searchQuery || selectedTag) && (
          <button type="button" onClick={handleClearSearch} style={styles.clearBtn}>
            Clear
          </button>
        )}
      </form>

      {/* Tag Filter */}
      {allTags.length > 0 && (
        <div style={styles.tagFilter}>
          <span style={styles.tagFilterLabel}>Filter by tag:</span>
          <div style={styles.tagList}>
            {allTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => handleTagFilter(tag)}
                style={{
                  ...styles.tagButton,
                  ...(selectedTag?.id === tag.id ? styles.tagButtonActive : {})
                }}
              >
                {tag.name}
                {tag.recipe_count > 0 && (
                  <span style={styles.tagCount}>({tag.recipe_count})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {recipes.length === 0 ? (
        <div style={styles.empty}>
          <p>No recipes found.</p>
          <Link to="/recipes/new">Create your first recipe!</Link>
        </div>
      ) : (
        <div style={styles.grid}>
          {recipes.map(recipe => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      )}
    </div>
  );
};

const RecipeCard = ({ recipe }) => (
  <Link to={`/recipes/${recipe.id}`} style={styles.card}>
    {/* Thumbnail Image */}
    <div style={styles.thumbnailContainer}>
      <img 
        src={recipe.imageUrl || '/no-image.png'} 
        alt={recipe.name}
        style={styles.thumbnail}
      />
     </div>
    
    <div style={styles.cardContent}>
      <div style={styles.cardHeader}>
        <h3 style={styles.cardTitle}>{recipe.name}</h3>
        {recipe.canEdit && (
          <span style={styles.canEditBadge}>✏️</span>
        )}
      </div>
      <p style={styles.cardDesc}>
        {recipe.description || 'No description'}
      </p>
      {recipe.tags && recipe.tags.length > 0 && (
        <div style={styles.cardTags}>
          {recipe.tags.slice(0, 3).map(tag => (
            <span key={tag.id} style={styles.cardTag}>{tag.name}</span>
          ))}
          {recipe.tags.length > 3 && (
            <span style={styles.cardTagMore}>+{recipe.tags.length - 3}</span>
          )}
        </div>
      )}
      <div style={styles.cardMeta}>
        <span>👤 {recipe.authorName || 'Unknown'}</span>
        <span>🥕 {recipe.ingredientCount} ingredients</span>
        <span>📝 {recipe.stepCount} steps</span>
      </div>
    </div>
  </Link>
);

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem 2rem'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  title: {
    fontSize: '1.8rem',
    color: '#1e3a5f',
    margin: 0
  },
  createBtn: {
    backgroundColor: '#22c55e',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600'
  },
  searchForm: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem'
  },
  searchInput: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    fontSize: '1rem'
  },
  searchBtn: {
    backgroundColor: '#2563eb',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600'
  },
  clearBtn: {
    backgroundColor: '#6b7280',
    color: 'white',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  empty: {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1.5rem'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    textDecoration: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    display: 'block',
    overflow: 'hidden'
  },
  thumbnailContainer: {
    width: '100%',
    height: '180px',
    overflow: 'hidden',
    backgroundColor: '#f3f4f6'
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  noImage: {
    width: '100%',
    height: '180px',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  noImageIcon: {
    fontSize: '3rem',
    opacity: 0.3
  },
  cardContent: {
    padding: '1rem 1.5rem 1.5rem'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  cardTitle: {
    fontSize: '1.2rem',
    color: '#1e3a5f',
    margin: '0 0 0.5rem 0'
  },
  canEditBadge: {
    fontSize: '1rem'
  },
  cardDesc: {
    color: '#6b7280',
    fontSize: '0.9rem',
    margin: '0 0 0.75rem 0',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  cardTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.35rem',
    marginBottom: '0.75rem'
  },
  cardTag: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.7rem',
    fontWeight: '500'
  },
  cardTagMore: {
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.7rem'
  },
  cardMeta: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.8rem',
    color: '#9ca3af',
    flexWrap: 'wrap'
  },
  tagFilter: {
    marginBottom: '1.5rem'
  },
  tagFilterLabel: {
    display: 'block',
    fontSize: '0.875rem',
    color: '#6b7280',
    marginBottom: '0.5rem'
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  tagButton: {
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    color: '#374151',
    padding: '0.35rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  tagButtonActive: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
    color: 'white'
  },
  tagCount: {
    marginLeft: '0.25rem',
    opacity: 0.7
  }
};

export default RecipeList;
